import L from 'leaflet'; // Keep L imported here
import 'leaflet.polylinemeasure';

export function addMiscControls(map: L.Map) {
    L.control.scale().addTo(map);

    // @ts-ignore - leaflet.polylinemeasure may not have up-to-date types
    const measureControl = L.control.polylineMeasure({
        position: 'topleft',
        unit: 'nauticalmiles',
        showBearings: true,
        clearMeasurementsOnStop: false,
        showUnitControl: true,
        measureControlTitleOn: 'Start measuring',
        measureControlTitleOff: 'Stop measuring',
    });
    measureControl.addTo(map);

    // @ts-ignore - Prevent map dragging when dragging measure points
    map.on('polylinemeasure:dragstart', () => {
        map.dragging.disable();
    });

    // @ts-ignore
    map.on('polylinemeasure:dragend', () => {
        map.dragging.enable();
    });

    // Add Custom Locate Control
    addLocateControl(map);

    // Add Coastguard Control
    addCoastguardControl(map);

    // Add Coords Control
    addCoordsControl(map);

    // Add Intro Control
    addIntroControl(map);

    return measureControl; // Return the control instance
}

function addLocateControl(map: L.Map) {
    const LocateControl = L.Control.extend({
        options: {
            position: 'topleft'
        },

        onAdd: function () {
            const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control');
            const button = L.DomUtil.create('a', 'leaflet-control-locate', container);
            button.href = '#';
            button.title = 'My location';
            button.role = 'button';
            button.innerHTML = '📍';

            let isLocating = false;
            let lastCenterTime = 0;
            let locationMarker: L.Marker | null = null;
            let locationCircle: L.Circle | null = null;
            let lastKnownLatLng: L.LatLng | null = null;
            let autoCenterInterval: ReturnType<typeof setInterval> | null = null;

            const onLocationFound = (e: L.LocationEvent) => {
                const radius = e.accuracy;
                const latlng = e.latlng;
                lastKnownLatLng = latlng;

                // Update or create marker
                if (locationMarker) {
                    locationMarker.setLatLng(latlng);
                    locationMarker.setPopupContent("You are within " + radius + " meters from this point");
                } else {
                    locationMarker = L.marker(latlng).addTo(map)
                        .bindPopup("You are within " + radius + " meters from this point");
                }

                // Update or create accuracy circle
                if (locationCircle) {
                    locationCircle.setLatLng(latlng);
                    locationCircle.setRadius(radius);
                } else {
                    locationCircle = L.circle(latlng, radius).addTo(map);
                }
            };

            const onLocationError = (e: L.ErrorEvent) => {
                alert(e.message);
                stopLocating();
            };

            const checkAutoCenter = () => {
                if (lastKnownLatLng) {
                    const now = Date.now();
                    if (now - lastCenterTime > 5000) { // 5 seconds debounce
                        map.setView(lastKnownLatLng, map.getZoom()); // Maintain current zoom level
                        lastCenterTime = now;
                    }
                }
            };

            const startLocating = () => {
                isLocating = true;
                L.DomUtil.addClass(button, 'active'); // For green background
                map.on('locationfound', onLocationFound);
                map.on('locationerror', onLocationError);
                map.locate({
                    setView: false, // Do not set view on location found
                    watch: true, // Keep watching for location changes
                    timeout: 30000,
                    enableHighAccuracy: true, // Request high accuracy
                    maximumAge: 0 // Do not use cached location
                });

                // Start the auto-center interval
                if (!autoCenterInterval) {
                    autoCenterInterval = setInterval(checkAutoCenter, 1000); // Check every second
                }
                // Force immediate center if location is already available
                if (lastKnownLatLng) {
                    map.setView(lastKnownLatLng, map.getZoom());
                    lastCenterTime = Date.now();
                }
            };

            const stopLocating = () => {
                isLocating = false;
                L.DomUtil.removeClass(button, 'active'); // Remove green background

                if (autoCenterInterval) {
                    clearInterval(autoCenterInterval);
                    autoCenterInterval = null;
                }

                map.stopLocate();
                map.off('locationfound', onLocationFound);
                map.off('locationerror', onLocationError);

                if (locationMarker) {
                    map.removeLayer(locationMarker);
                    locationMarker = null;
                }
                if (locationCircle) {
                    map.removeLayer(locationCircle);
                    locationCircle = null;
                }
                lastKnownLatLng = null;
            };

            L.DomEvent.on(button, 'click', (e) => {
                L.DomEvent.stop(e); // Prevent default browser action and map zoom
                if (isLocating) {
                    stopLocating();
                } else {
                    startLocating();
                }
            });

            return container;
        }
    });

    new LocateControl().addTo(map);
}

function addCoastguardControl(map: L.Map) {
    const CoastguardControl = L.Control.extend({
        options: {
            position: 'topleft'
        },

        onAdd: function () {
            const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control');
            const button = L.DomUtil.create('a', 'gear-button', container);
            button.href = '#';
            button.title = "Coastguard Tools";
            button.role = 'button';
            button.innerHTML = '🛟';

            L.DomEvent.disableClickPropagation(button);
            L.DomEvent.on(button, 'click', (e) => {
                L.DomEvent.stop(e);
                const event = new CustomEvent('open-coastguard-panel');
                map.getContainer().dispatchEvent(event);
            });

            return container;
        }
    });

    new CoastguardControl().addTo(map);
}

function addCoordsControl(map: L.Map) {
    const CoordsControl = L.Control.extend({
        options: {
            position: 'topleft'
        },

        onAdd: function () {
            const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control');
            const button = L.DomUtil.create('a', 'gear-button', container); // Reused gear-button class
            button.href = '#';
            button.title = "Current Position";
            button.role = 'button';
            button.innerHTML = '🌐'; // Globe icon

            L.DomEvent.disableClickPropagation(button);
            L.DomEvent.on(button, 'click', (e) => {
                L.DomEvent.stop(e);
                const event = new CustomEvent('open-coords-panel');
                map.getContainer().dispatchEvent(event);
            });

            return container;
        }
    });

    new CoordsControl().addTo(map);
}

export function addChartLayersControl(map: L.Map) {
    const ChartLayersControl = L.Control.extend({
        options: {
            position: 'topleft'
        },

        onAdd: function () {
            const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control');
            container.id = 'chart-layers-control'; // ID for toggling visibility
            container.style.display = 'none'; // Hidden by default

            const button = L.DomUtil.create('a', 'gear-button', container); // Reused gear-button class
            button.href = '#';
            button.title = "Chart Layers";
            button.role = 'button';
            button.innerHTML = '<img src="/sliders.png" alt="Chart Layers" width="20" height="20" style="margin-top: 5px;">';

            L.DomEvent.disableClickPropagation(button);
            L.DomEvent.on(button, 'click', (e) => {
                L.DomEvent.stop(e);
                const event = new CustomEvent('open-chart-layers-panel');
                map.getContainer().dispatchEvent(event);
            });

            return container;
        }
    });

    new ChartLayersControl().addTo(map);
}

function addIntroControl(map: L.Map) {
    const IntroControl = L.Control.extend({
        options: {
            position: 'topleft'
        },

        onAdd: function () {
            const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control');
            const button = L.DomUtil.create('a', 'gear-button', container); // Reused gear-button class
            button.href = '#';
            button.title = "Introduction";
            button.role = 'button';
            button.innerHTML = 'ℹ️'; // Info icon

            L.DomEvent.disableClickPropagation(button);
            L.DomEvent.on(button, 'click', (e) => {
                L.DomEvent.stop(e);
                const event = new CustomEvent('open-intro-panel');
                map.getContainer().dispatchEvent(event);
            });

            return container;
        }
    });

    new IntroControl().addTo(map);
}
